import { useRef } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'

/**
 * Build the visible page list with ellipsis gaps. Up to 7 pages render
 * in full; beyond that it collapses to `1 … 4 5 6 … 20` style, always
 * keeping the first page, last page and a window around the current.
 *
 * Returns an array of `{ page, key }` and `{ gap: true, key }` items.
 */
function buildPageItems(current, total) {
  const span =
    total <= 7
      ? Array.from({ length: total }, (_, i) => i + 1)
      : (() => {
          const set = new Set([1, total, current, current - 1, current + 1])
          if (current <= 3) {
            set.add(2)
            set.add(3)
          }
          if (current >= total - 2) {
            set.add(total - 1)
            set.add(total - 2)
          }
          return [...set]
            .filter((p) => p >= 1 && p <= total)
            .sort((a, b) => a - b)
        })()
  const items = []
  let prev = 0
  for (const p of span) {
    if (p - prev > 1) items.push({ gap: true, key: `gap-${p}` })
    items.push({ page: p, key: `p-${p}` })
    prev = p
  }
  return items
}

/**
 * Reusable server-side pagination bar — sits at the bottom of a table
 * card (it carries its own top border). Renders the "Showing X to Y of
 * Z" summary, an optional rows-per-page selector, and Previous / page
 * numbers / Next controls.
 *
 * @param {number}   page          Current page (1-based).
 * @param {number}   totalPages    Total page count.
 * @param {number}   total         Total row count across all pages.
 * @param {number}   limit         Rows per page.
 * @param {Function} onPageChange  (nextPage) => void.
 * @param {Function} [onLimitChange] (nextLimit) => void. Omit to hide
 *                                 the rows-per-page selector.
 * @param {boolean}  [loading]     Disables the controls while a page
 *                                 request is in flight.
 * @param {number[]} [limitOptions] Rows-per-page choices.
 */
/**
 * Scroll back to the top of whatever is actually scrolling, starting from
 * the pagination bar and walking up.
 *
 * Changing page left the reader parked at the BOTTOM of the list, staring at
 * the last rows of the new page with its first rows above the viewport
 * (QA, 2026-09-01). The layouts already do this on route change, but paging
 * is state, not navigation, so it never fired.
 *
 * It walks to the nearest scrollable ancestor rather than assuming the page
 * shell for two reasons: this app scrolls `<main class="app-main-scroll">`
 * and NOT the window, so `window.scrollTo` is a no-op; and the same
 * component is used inside modals, where yanking the page behind the dialog
 * would be wrong. Falls back to the window for any caller that does scroll
 * it.
 *
 * Instant, not smooth — it matches the layouts' route-change behaviour and
 * sidesteps `prefers-reduced-motion` entirely.
 */
function scrollListToTop(node) {
  let el = node?.parentElement
  while (el && el !== document.body) {
    const { overflowY } = getComputedStyle(el)
    const scrollable = overflowY === 'auto' || overflowY === 'scroll'
    if (scrollable && el.scrollHeight > el.clientHeight) {
      el.scrollTo({ top: 0, left: el.scrollLeft })
      return
    }
    el = el.parentElement
  }
  window.scrollTo({ top: 0, left: window.scrollX })
}

export function Pagination({
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange,
  loading = false,
  // Canonical page-size options across every listing surface. Picked
  // to cover the common patterns: 25/50 for skim-the-page, 100/200
  // for power-user triage, 500 for "I want to ctrl+F across the
  // whole set". Pages that need a non-standard set can still pass
  // their own `limitOptions`.
  limitOptions = [25, 50, 100, 200, 500],
}) {
  const safeTotalPages = Math.max(totalPages || 1, 1)
  const from = total === 0 ? 0 : (page - 1) * limit + 1
  const to = Math.min(page * limit, total)
  const items = buildPageItems(page, safeTotalPages)

  const barRef = useRef(null)

  function go(target) {
    if (loading) return
    const next = Math.min(Math.max(target, 1), safeTotalPages)
    if (next === page) return
    onPageChange(next)
    scrollListToTop(barRef.current)
  }

  const navBtn =
    'inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-[color:var(--color-rule)] text-xs font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div
      ref={barRef}
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[color:var(--color-rule)] bg-[color:var(--color-card)]"
    >
      {/* Numeric spans wear the KPI-tile number treatment
       *  (`font-display tabular-nums`) so "Showing 1 to 1 of 1" reads
       *  in the same numeric face as the cards above the table —
       *  paired display-font numerals across the whole platform-admin
       *  surface. Surrounding "Showing / to / of / results" stays in
       *  the body font so only the figures get the display flourish. */}
      <p className="text-xs text-[color:var(--color-ink-muted)]">
        {total === 0 ? (
          'No records found.'
        ) : (
          <>
            Showing{' '}
            <span className="font-display font-semibold tabular-nums text-[color:var(--color-ink-strong)]">
              {from}
            </span>{' '}
            to{' '}
            <span className="font-display font-semibold tabular-nums text-[color:var(--color-ink-strong)]">
              {to}
            </span>{' '}
            of{' '}
            <span className="font-display font-semibold tabular-nums text-[color:var(--color-ink-strong)]">
              {total}
            </span>{' '}
            results
          </>
        )}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        {onLimitChange ? (
          <label className="flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]">
            Rows per page
            {/* `appearance-none` + a following-sibling chevron so the
             *  global rotation CSS (`select[data-open] ~ svg`) spins the
             *  arrow when the popup opens — matching every other dropdown
             *  in the app. */}
            <span className="relative inline-flex items-center">
              <select
                value={limit}
                disabled={loading}
                onChange={(e) => {
                  onLimitChange(Number(e.target.value))
                  // Resizing the page re-slices the list from row 1, so the
                  // reader belongs at the top of it just as much.
                  scrollListToTop(barRef.current)
                }}
                className="h-8 pl-2 pr-7 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-50 appearance-none"
              >
                {limitOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={13}
                aria-hidden="true"
                className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[color:var(--color-ink-muted)]"
              />
            </span>
          </label>
        ) : null}

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => go(page - 1)}
            disabled={loading || page <= 1}
            className={navBtn}
          >
            <ChevronLeft size={14} />
            Previous
          </button>

          {/* Full numbered pager — desktop / tablet. Hidden on mobile,
           *  where the compact "page / total" indicator below replaces it
           *  so the row never overflows a narrow screen. */}
          <div className="hidden sm:flex items-center gap-1">
            {items.map((it) =>
              it.gap ? (
                <span
                  key={it.key}
                  className="px-1.5 text-xs text-[color:var(--color-ink-subtle)] select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => go(it.page)}
                  disabled={loading}
                  aria-current={it.page === page ? 'page' : undefined}
                  // Page-number buttons use `font-display tabular-nums`
                  // so the figure inside the pill ("1", "12", "100")
                  // wears the same display-font numerals the KPI tiles
                  // use above the table — page numbers and KPI numbers
                  // read as one numeric family.
                  className={
                    'h-8 min-w-8 px-2 rounded-md text-xs font-display font-semibold tabular-nums cursor-pointer disabled:cursor-not-allowed transition-colors ' +
                    (it.page === page
                      ? 'bg-[color:var(--color-primary)] text-white'
                      : 'border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-40')
                  }
                >
                  {it.page}
                </button>
              ),
            )}
          </div>

          {/* Compact indicator — mobile only. */}
          <span className="sm:hidden px-2 text-xs font-display font-semibold tabular-nums text-[color:var(--color-ink-strong)] whitespace-nowrap">
            {page} / {safeTotalPages}
          </span>

          <button
            type="button"
            onClick={() => go(page + 1)}
            disabled={loading || page >= safeTotalPages}
            className={navBtn}
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
